import { useTranslation } from 'react-i18next';

import { Box, Flex, Link, Text } from '@bonusly/design-system';

import type { ApprovalDecision, CardNode } from '../hooks/use-bizy-global-chat';
import { BizyApprovalCard } from './bizy-approval-card';
import { BizyContentCard } from './bizy-content-card';
import { BizyDraftCard, type DraftVariant } from './bizy-draft-card';
import { BizyMeetingPeopleCard, type MeetingPerson } from './bizy-meeting-people-card';
import { BizyRecognitionInline } from './bizy-recognition-inline';

interface BizyCardRendererProps {
	cards: CardNode[];
	messageKey?: string;
	/** Position of the first card in this batch within the parent
	 *  document's overall card sequence. Lets choice updates target the
	 *  right entry in the message's `nodeChoices` map even when the
	 *  parent renders a subset. */
	cardIndexOffset?: number;
	onConfirm?: (cardIndex: number, confirmMessage: string) => void;
	onChoiceUpdate?: (messageKey: string, cardIndex: number, choice: ApprovalDecision) => void;
}

export function BizyCardRenderer({ cards, messageKey, cardIndexOffset = 0, onConfirm, onChoiceUpdate }: BizyCardRendererProps) {
	const { t } = useTranslation('admin_bizy');

	return (
		<>
			{cards.map((card, localIndex) => {
				const cardIndex = localIndex + cardIndexOffset;
				if (card.type === 'approval_card') {
					const p = card.payload as {
						variant?: string;
						title?: string;
						recipients?: string[];
						amount?: number;
						message?: string;
						hashtags?: string[];
						footer?: string;
						metadata?: { key: string; value: string }[];
						confirmLabel?: string;
						confirmMessage?: string;
					};
					const isRec = p.variant === 'recognition';
					return (
						<Box key={`card-${cardIndex}`} my={2}>
							<BizyApprovalCard
								title={p.title ?? t('approval_card.default_title')}
								metadata={!isRec ? p.metadata : undefined}
								footer={p.footer}
								confirmLabel={p.confirmLabel}
								receiptLabel={
									isRec && p.recipients?.length ? t('approval_card.recognition_sent', { recipients: p.recipients.map((r) => `@${r}`).join(', ') }) : undefined
								}
								choice={card.choice}
								onConfirm={() => {
									if (messageKey && onChoiceUpdate) onChoiceUpdate(messageKey, cardIndex, 'approved');
									if (p.confirmMessage && onConfirm) onConfirm(cardIndex, p.confirmMessage);
								}}
							>
								{isRec && <BizyRecognitionInline recipients={p.recipients} amount={p.amount} message={p.message} hashtags={p.hashtags} />}
							</BizyApprovalCard>
						</Box>
					);
				}

				if (card.type === 'content_card') {
					const p = card.payload as {
						variant?: string;
						contentType?: 'recognition' | 'meeting' | 'generic';
						title?: string;
						giverName?: string;
						recipients?: string[];
						amount?: number;
						message?: string;
						hashtags?: string[];
						date?: string;
						content?: string;
						href?: string;
						metadata?: { key: string; value: string }[];
					};

					if (p.variant === 'recognition') {
						return (
							<Box key={`card-${cardIndex}`} my={2}>
								<Box border="1px solid" borderColor="border.soft" borderRadius={12} bg="bg.surface" overflow="hidden" px={3} py={3}>
									<Text fontSize="sm" color="text.default" lineHeight="tall">
										{p.giverName && (
											<Text as="span" fontSize="inherit" fontWeight="semibold">
												{p.giverName}:{' '}
											</Text>
										)}
										<BizyRecognitionInline recipients={p.recipients} amount={p.amount} message={p.message} hashtags={p.hashtags} />
									</Text>
									<Flex align="center" justify="space-between" mt={2}>
										<Text fontSize="xs" color="text.softest">
											{p.date}
										</Text>
										{p.href && (
											<Link href={p.href} fontSize="xs" color="text.interactive" target="_blank" rel="noopener noreferrer">
												{t('approval_card.view')}
											</Link>
										)}
									</Flex>
								</Box>
							</Box>
						);
					}

					return (
						<Box key={`card-${cardIndex}`} my={2}>
							<BizyContentCard type={p.contentType ?? 'generic'} title={p.title} content={p.content ?? ''} href={p.href} metadata={p.metadata} />
						</Box>
					);
				}

				if (card.type === 'draft_card') {
					const p = card.payload as {
						variants?: DraftVariant[];
						confirmLabel?: string;
						confirmMessage?: string;
					};
					if (!p.variants?.length) return null;
					return (
						<Box key={`card-${cardIndex}`} my={2}>
							<BizyDraftCard
								variants={p.variants}
								confirmLabel={p.confirmLabel}
								choice={card.choice}
								onConfirm={() => {
									// BizyDraftCard calls onConfirm for both Edit-in-Give-Box and
									// Give Now, so this is the single place that records the choice.
									if (messageKey && onChoiceUpdate) onChoiceUpdate(messageKey, cardIndex, 'approved');
								}}
								onGiveNow={(msg) => {
									// Choice was already recorded by onConfirm (called first from
									// handleGiveNow); don't duplicate the state update.
									if (onConfirm) onConfirm(cardIndex, msg);
								}}
							/>
						</Box>
					);
				}

				if (card.type === 'meeting_people_card') {
					const p = card.payload as {
						title?: string;
						people?: MeetingPerson[];
					};
					if (!p.people?.length) return null;
					return (
						<Box key={`card-${cardIndex}`} my={3} ml={1}>
							<BizyMeetingPeopleCard
								title={p.title ?? t('meeting_people_card.default_title')}
								people={p.people}
								cardIndex={cardIndex}
								messageKey={messageKey}
								choice={card.choice}
								onChoiceUpdate={onChoiceUpdate}
							/>
						</Box>
					);
				}

				return null;
			})}
		</>
	);
}
